import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, Card, CardContent, Chip, List, ListItem, ListItemText, Divider, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import {
  Inventory as InventoryIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import api from '../services/api';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
  ArcElement, PointElement, LineElement
);

function Dashboard() {
  const [metricas, setMetricas] = useState(null);
  const [stockCategoria, setStockCategoria] = useState([]);
  const [productosTop, setProductosTop] = useState([]);
  const [ventasMensuales, setVentasMensuales] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [alertasStock, setAlertasStock] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para reporte diario
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
  const [reporteDiario, setReporteDiario] = useState(null);
  const [loadingReporte, setLoadingReporte] = useState(false);

  useEffect(() => {
    cargarDatos();
    cargarReporteDiario(fechaSeleccionada);
  }, []);

  useEffect(() => {
    cargarReporteDiario(fechaSeleccionada);
  }, [fechaSeleccionada]);

  const cargarDatos = async () => {
    try {
      const [resMetricas, resStock, resTop, resVentas, resPagos, resAlertas, resMov] = await Promise.all([
        api.get('/dashboard/metricas'),
        api.get('/dashboard/stock-categoria'),
        api.get('/dashboard/productos-mas-vendidos'),
        api.get('/dashboard/ventas-mensuales'),
        api.get('/dashboard/metodos-pago'),
        api.get('/dashboard/alertas-stock'),
        api.get('/dashboard/movimientos-recientes'),
      ]);
      setMetricas(resMetricas.data.data);
      setStockCategoria(resStock.data.data);
      setProductosTop(resTop.data.data);
      setVentasMensuales(resVentas.data.data);
      setMetodosPago(resPagos.data.data);
      setAlertasStock(resAlertas.data.data);
      setMovimientos(resMov.data.data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FIX: Función para cargar reporte diario
  // Extrae año/mes/día directamente sin problemas de timezone
  // ============================================
  const cargarReporteDiario = async (fecha) => {
    try {
      setLoadingReporte(true);
      
      // FIX: Extraer año, mes, día directamente sin timezone issues
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, '0');
      const day = String(fecha.getDate()).padStart(2, '0');
      const fechaStr = `${year}-${month}-${day}`;
      
      console.log('Fecha seleccionada:', fecha);
      console.log('Fecha enviada al backend:', fechaStr);
      
      const res = await api.get(`/dashboard/ventas-diarias/${fechaStr}`);
      setReporteDiario(res.data.data);
    } catch (error) {
      console.error('Error cargando reporte diario:', error);
      setReporteDiario(null);
    } finally {
      setLoadingReporte(false);
    }
  };

  if (loading) return <Typography sx={{ p: 4 }}>Cargando dashboard...</Typography>;

  // KPIs
  const cards = [
    { title: 'Total Productos', value: metricas?.totalProductos || 0, icon: <InventoryIcon sx={{ fontSize: 40, color: '#1976d2' }} />, color: '#e3f2fd' },
    { title: 'Total Clientes', value: metricas?.totalClientes || 0, icon: <PeopleIcon sx={{ fontSize: 40, color: '#388e3c' }} />, color: '#e8f5e9' },
    { title: 'Valor Inventario', value: `S/ ${parseFloat(metricas?.valorInventario || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`, icon: <MoneyIcon sx={{ fontSize: 40, color: '#f57c00' }} />, color: '#fff3e0' },
    { title: 'Ventas del Mes', value: `S/ ${parseFloat(metricas?.ventasMes || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`, icon: <TrendingIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />, color: '#f3e5f5' },
    { title: 'Cotiz. Pendientes', value: metricas?.cotizacionesPendientes || 0, icon: <ReceiptIcon sx={{ fontSize: 40, color: '#1976d2' }} />, color: '#e3f2fd' },
    { title: 'Alertas Stock', value: metricas?.productosStockBajo || 0, icon: <WarningIcon sx={{ fontSize: 40, color: '#d32f2f' }} />, color: '#ffebee' },
  ];

  // Gráfico 1: Stock por Categoría (Barras)
  const barData = {
    labels: stockCategoria.map(cat => cat.categoria),
    datasets: [{
      label: 'Stock Total',
      data: stockCategoria.map(cat => parseInt(cat.stock_total)),
      backgroundColor: ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f'],
      borderRadius: 4,
    }],
  };

  // Gráfico 2: Ventas Mensuales (Línea)
  const lineData = {
    labels: ventasMensuales.map(v => {
      const [year, month] = v.mes.split('-');
      return `${month}/${year}`;
    }),
    datasets: [{
      label: 'Ventas (S/)',
      data: ventasMensuales.map(v => parseFloat(v.total_ventas)),
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 5,
      pointBackgroundColor: '#1976d2',
    }],
  };

  // Gráfico 3: Productos Más Vendidos (Barras Horizontales)
  const topProductsData = {
    labels: productosTop.map(p => p.nombre.length > 20 ? p.nombre.substring(0, 20) + '...' : p.nombre),
    datasets: [{
      label: 'Unidades Vendidas',
      data: productosTop.map(p => parseInt(p.total_vendido)),
      backgroundColor: '#388e3c',
      borderRadius: 4,
    }],
  };

  const topProductsOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: { legend: { display: false } },
  };

  // Gráfico 4: Métodos de Pago (Doughnut)
  const doughnutData = {
    labels: metodosPago.map(m => m.metodo_pago.charAt(0).toUpperCase() + m.metodo_pago.slice(1)),
    datasets: [{
      data: metodosPago.map(m => parseFloat(m.monto)),
      backgroundColor: ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f'],
      borderWidth: 2,
    }],
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Dashboard - Inteligencia de Negocios
      </Typography>

      {/* KPIs */}
      <Grid container spacing={2}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ backgroundColor: card.color, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">{card.title}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{card.value}</Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Gráficos Principales */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Ventas Mensuales */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
              📈 Ventas Mensuales (Últimos 6 meses)
            </Typography>
            {ventasMensuales.length > 0 ? (
              <Line data={lineData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
            ) : (
              <Alert severity="info">No hay datos de ventas registrados aún</Alert>
            )}
          </Paper>
        </Grid>

        {/* Métodos de Pago */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
              💳 Métodos de Pago
            </Typography>
            {metodosPago.length > 0 ? (
              <Doughnut data={doughnutData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
            ) : (
              <Alert severity="info">No hay pagos registrados</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Segunda fila de gráficos */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Productos Más Vendidos */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#388e3c' }}>
              🏆 Productos Más Vendidos (Top 5)
            </Typography>
            {productosTop.length > 0 ? (
              <Bar data={topProductsData} options={topProductsOptions} />
            ) : (
              <Alert severity="info">No hay ventas registradas aún</Alert>
            )}
          </Paper>
        </Grid>

        {/* Stock por Categoría */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f57c00' }}>
              📦 Stock por Categoría
            </Typography>
            <Bar data={barData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
          </Paper>
        </Grid>
      </Grid>

      {/* Tercera fila: Alertas y Movimientos */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Alertas de Stock Bajo */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#d32f2f' }}>
              ⚠️ Alertas de Stock Bajo
            </Typography>
            {alertasStock.length > 0 ? (
              <List dense>
                {alertasStock.map((prod) => (
                  <ListItem key={prod.id} sx={{ backgroundColor: '#ffebee', mb: 1, borderRadius: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontWeight="bold">{prod.codigo} - {prod.nombre}</Typography>
                          <Chip label={`Stock: ${prod.stock_actual}`} color="error" size="small" />
                        </Box>
                      }
                      secondary={`Mínimo: ${prod.stock_minimo} | Categoría: ${prod.categoria_nombre} | Ubicación: ${prod.ubicacion_almacen}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="success">✅ No hay alertas de stock. Todo está en orden.</Alert>
            )}
          </Paper>
        </Grid>

        {/* Movimientos Recientes del Kardex */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
              🔄 Movimientos Recientes del Kardex
            </Typography>
            {movimientos.length > 0 ? (
              <List dense>
                {movimientos.slice(0, 5).map((mov) => (
                  <ListItem key={mov.id} sx={{ mb: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="bold">{mov.producto_nombre}</Typography>
                          <Chip 
                            label={mov.tipo_movimiento.toUpperCase()} 
                            color={mov.tipo_movimiento === 'entrada' ? 'success' : mov.tipo_movimiento === 'salida' ? 'error' : 'warning'} 
                            size="small" 
                          />
                        </Box>
                      }
                      secondary={`Cantidad: ${mov.cantidad} | Stock: ${mov.stock_anterior} → ${mov.stock_nuevo} | ${new Date(mov.created_at).toLocaleString('es-PE')}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">No hay movimientos registrados</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {
      /* ============================================
          REPORTE DIARIO DE VENTAS
          ============================================ */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                📅 Reporte Diario de Ventas
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  label="Seleccionar fecha"
                  value={fechaSeleccionada}
                  onChange={(newValue) => setFechaSeleccionada(newValue)}
                  renderInput={(params) => <TextField {...params} size="small" sx={{ width: 200 }} />}
                />
              </LocalizationProvider>
            </Box>

            {loadingReporte ? (
              <Typography sx={{ py: 4, textAlign: 'center' }}>Cargando reporte...</Typography>
            ) : !reporteDiario ? (
              <Alert severity="info">No hay datos para la fecha seleccionada</Alert>
            ) : (
              <>
                {/* KPIs del día */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ backgroundColor: '#e3f2fd', textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">Total Ventas</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          S/ {parseFloat(reporteDiario.resumen.total_ventas || 0).toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ backgroundColor: '#e8f5e9', textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">Transacciones</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                          {reporteDiario.resumen.total_transacciones || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ backgroundColor: '#fff3e0', textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">IGV</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                          S/ {parseFloat(reporteDiario.resumen.total_igv || 0).toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ backgroundColor: '#f3e5f5', textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">Clientes</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                          {reporteDiario.resumen.total_clientes || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Gráfico de ventas por hora */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      📊 Ventas por Hora
                    </Typography>
                    <Bar 
                      data={{
                        labels: reporteDiario.ventasPorHora.map(v => v.hora),
                        datasets: [{
                          label: 'Ventas (S/)',
                          data: reporteDiario.ventasPorHora.map(v => v.total_ventas),
                          backgroundColor: '#1976d2',
                          borderRadius: 4,
                        }]
                      }}
                      options={{
                        responsive: true,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, ticks: { callback: (v) => `S/ ${v}` } }
                        }
                      }}
                    />
                  </Grid>

                  {/* Métodos de pago del día */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      💳 Métodos de Pago
                    </Typography>
                    {reporteDiario.metodosPago.length > 0 ? (
                      <Doughnut 
                        data={{
                          labels: reporteDiario.metodosPago.map(m => m.metodo_pago.charAt(0).toUpperCase() + m.metodo_pago.slice(1)),
                          datasets: [{
                            data: reporteDiario.metodosPago.map(m => parseFloat(m.monto)),
                            backgroundColor: ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f'],
                          }]
                        }}
                        options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
                      />
                    ) : (
                      <Alert severity="info">Sin pagos registrados</Alert>
                    )}
                  </Grid>
                </Grid>

                {/* Top productos del día */}
                {reporteDiario.topProductos.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      🏆 Productos Más Vendidos del Día
                    </Typography>
                    <Grid container spacing={2}>
                      {reporteDiario.topProductos.map((prod, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={prod.id}>
                          <Card sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                            {prod.imagen ? (
                              <Avatar 
                                src={`http://localhost:5000${prod.imagen}`} 
                                alt={prod.nombre}
                                sx={{ width: 56, height: 56, mr: 2 }}
                                variant="rounded"
                              />
                            ) : (
                              <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: '#e0e0e0' }}>
                                {prod.nombre.charAt(0)}
                              </Avatar>
                            )}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight="bold">{prod.nombre}</Typography>
                              <Typography variant="caption" color="text.secondary">{prod.codigo}</Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Chip label={`${prod.cantidad_vendida} uds`} size="small" color="primary" />
                                <Typography variant="body2" color="primary" fontWeight="bold">
                                  S/ {parseFloat(prod.total_ventas).toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Tabla de facturas del día */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    🧾 Facturas del Día
                  </Typography>
                  {reporteDiario.facturas.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: '#1976d2' }}>
                          <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Hora</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Método</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reporteDiario.facturas.map((fac) => (
                            <TableRow key={fac.id} hover>
                              <TableCell>{fac.codigo}</TableCell>
                              <TableCell>{fac.cliente_nombre}</TableCell>
                              <TableCell>{new Date(fac.fecha_emision).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={fac.metodo_pago} 
                                  size="small" 
                                  color={fac.metodo_pago === 'efectivo' ? 'success' : fac.metodo_pago === 'transferencia' ? 'info' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                S/ {parseFloat(fac.total).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No hay facturas/boletas para esta fecha</Alert>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Información del Sistema */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              ℹ️ Información del Sistema
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  <strong>Proyecto:</strong> "Implementación de un sistema web integral de gestión comercial: control de inventarios mediante Kardex digital y automatización del ciclo de ventas con inteligencia de negocios en EJARAD-TIC, Piura 2026"
                </Typography>
                <Typography variant="body1">
                  <strong></strong>
                </Typography>
                <ul>
                  
                </ul>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  <strong>Empresa:</strong> EJARAD-TIC Soluciones Informáticas
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Ubicación:</strong> Mz AS22 Monteverde Galilea - Castilla - Piura, Perú
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Año:</strong> 2026
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sistema desarrollado como parte de las prácticas preprofesionales - Ingeniería de Sistemas
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;